import {TimesheetTaskPredefinedDescription} from "../../models/timesheet/TimesheetTaskPredefinedDescription";
import {
    errorFetchingRequestState,
    getInitialRequestState,
    RequestState,
    startFetchingRequestState,
    successFetchingRequestState
} from "../../models/common/RequestState";
import {
    CREATE_TASK_PREDEF_DESCRIPTION,
    CREATE_TASK_PREDEF_DESCRIPTION_ERROR,
    CREATE_TASK_PREDEF_DESCRIPTION_SUCCESS,
    DELETE_TASK_PREDEF_DESCRIPTION,
    DELETE_TASK_PREDEF_DESCRIPTION_ERROR,
    DELETE_TASK_PREDEF_DESCRIPTION_SUCCESS,
    FETCH_TASK_PREDEF_DESCRIPTION,
    FETCH_TASK_PREDEF_DESCRIPTION_ERROR,
    FETCH_TASK_PREDEF_DESCRIPTION_SUCCESS,
    INIT_FORM_TASK_PREDEF_DESCRIPTION,
    RESTORE_TASK_PREDEF_DESCRIPTION,
    RESTORE_TASK_PREDEF_DESCRIPTION_ERROR,
    RESTORE_TASK_PREDEF_DESCRIPTION_SUCCESS,
    TimesheetTaskPredefDescriptionActionTypes,
    UPDATE_TASK_PREDEF_DESCRIPTION,
    UPDATE_TASK_PREDEF_DESCRIPTION_ERROR,
    UPDATE_TASK_PREDEF_DESCRIPTION_SUCCESS
} from "../../actions/timesheet_tasks/TimesheetTaskPredefDescriptionActions";
import {Nullable} from "../../models/common/Nullable";
import {ApiReturnType} from "../../models/common/ApiReturnType";
import {changeReduxArrayItemState} from "../../helpers/ReduxReducerHelpers";
import {Identifier} from "../../models/common/Identifier";

export type TimesheetTaskPredefDescriptionStateItem = ApiReturnType< Nullable< TimesheetTaskPredefinedDescription[] > >

export interface TimesheetTaskPredefDescriptionState {
    descriptionsByTaskId: {
        [ key: number ]: TimesheetTaskPredefDescriptionStateItem
    }
    currentFormEntryByDescriptionId: {
        [ key: number ]: TimesheetTaskPredefinedDescription
    }
    fetchRequest: RequestState
    changingRequest: RequestState
}

const initialState: TimesheetTaskPredefDescriptionState = {
    descriptionsByTaskId: {},
    currentFormEntryByDescriptionId: {},
    fetchRequest: getInitialRequestState(),
    changingRequest: getInitialRequestState()
}

export function TimesheetTaskPredefDescriptionReducer(
    state: TimesheetTaskPredefDescriptionState = initialState,
    action: TimesheetTaskPredefDescriptionActionTypes
): TimesheetTaskPredefDescriptionState {

    switch( action.type ) {
        case FETCH_TASK_PREDEF_DESCRIPTION:
            return {
                ...state,
                descriptionsByTaskId: {
                    ...state.descriptionsByTaskId,
                    [ action.timesheetTaskId.rawValue ]: {
                        totalEntries: 0,
                        entries: null
                    }
                },
                fetchRequest: startFetchingRequestState()
            };
        case FETCH_TASK_PREDEF_DESCRIPTION_SUCCESS:
            return {
                ...state,
                descriptionsByTaskId: {
                    ...state.descriptionsByTaskId,
                    [ action.timesheetTaskId.rawValue ]: {
                        totalEntries: action.descriptions.totalEntries,
                        entries: action.descriptions.entries
                    }
                },
                fetchRequest: successFetchingRequestState()
            };
        case FETCH_TASK_PREDEF_DESCRIPTION_ERROR:
            return {
                ...state,
                fetchRequest: errorFetchingRequestState( action.error )
            };
        case DELETE_TASK_PREDEF_DESCRIPTION:
            return {
                ...state,
                changingRequest: startFetchingRequestState()
            };
        case DELETE_TASK_PREDEF_DESCRIPTION_SUCCESS: {
            const taskId = action.timesheetTaskId.rawValue;
            return {
                ...state,
                descriptionsByTaskId: {
                    ...state.descriptionsByTaskId,
                    [taskId]: {
                        ...state.descriptionsByTaskId[taskId],
                        entries: changePredefItemState(
                            state.descriptionsByTaskId[taskId].entries,
                            action.taskDescriptionId,
                            {isDeleted: true}
                        )
                    }
                },
                changingRequest: successFetchingRequestState()
            };
        }
        case DELETE_TASK_PREDEF_DESCRIPTION_ERROR:
            return {
                ...state,
                changingRequest: errorFetchingRequestState( action.error )
            };
        case RESTORE_TASK_PREDEF_DESCRIPTION:
            return {
                ...state,
                changingRequest: startFetchingRequestState()
            };
        case RESTORE_TASK_PREDEF_DESCRIPTION_SUCCESS: {
            const taskId = action.timesheetTaskId.rawValue;
            return {
                ...state,
                descriptionsByTaskId: {
                    ...state.descriptionsByTaskId,
                    [ taskId ]: {
                        ...state.descriptionsByTaskId[ taskId ],
                        entries: changePredefItemState(
                            state.descriptionsByTaskId[ taskId ].entries,
                            action.taskDescriptionId,
                            { isDeleted: false }
                        )
                    }
                },
                changingRequest: successFetchingRequestState()
            };
        }
        case RESTORE_TASK_PREDEF_DESCRIPTION_ERROR:
            return {
                ...state,
                changingRequest: errorFetchingRequestState( action.error )
            };
        case UPDATE_TASK_PREDEF_DESCRIPTION:
            return {
                ...state,
                changingRequest: startFetchingRequestState()
            };
        case UPDATE_TASK_PREDEF_DESCRIPTION_SUCCESS: {
            const taskId = action.timesheetTaskId.rawValue;
            return {
                ...state,
                descriptionsByTaskId: {
                    ...state.descriptionsByTaskId,
                    [ taskId ]: {
                        ...state.descriptionsByTaskId[ taskId ],
                        entries: changePredefItemState(
                            state.descriptionsByTaskId[ taskId ].entries,
                            action.preDefDescription.taskDescriptionId,
                            action.preDefDescription
                        )
                    }
                },
                changingRequest: successFetchingRequestState()
            };
        }
        case UPDATE_TASK_PREDEF_DESCRIPTION_ERROR:
            return {
                ...state,
                changingRequest: errorFetchingRequestState( action.error )
            };
        case CREATE_TASK_PREDEF_DESCRIPTION:
            return {
                ...state,
                changingRequest: startFetchingRequestState()
            };
        case CREATE_TASK_PREDEF_DESCRIPTION_SUCCESS:
            return {
                ...state,
                changingRequest: successFetchingRequestState()
            };
        case CREATE_TASK_PREDEF_DESCRIPTION_ERROR:
            return {
                ...state,
                changingRequest: errorFetchingRequestState( action.error )
            };
        case INIT_FORM_TASK_PREDEF_DESCRIPTION:
            return {
                ...state,
                currentFormEntryByDescriptionId: {
                    ...state.currentFormEntryByDescriptionId,
                    [ action.description.taskDescriptionId.rawValue ]: action.description
                }
            };
        default:
            return state;
    }
}

function changePredefItemState(
    haystack: Nullable< TimesheetTaskPredefinedDescription[] > ,
    needleId: Identifier<number>,
    newState: Partial< TimesheetTaskPredefinedDescription >
): TimesheetTaskPredefinedDescription[]
{
    if( haystack === null ) {
        return [];
    }

    return changeReduxArrayItemState(
        haystack,
        needleId.rawValue,
        "taskDescriptionId",
        newState
    );
}